<template>
	<div class="about container">
		<!-- <h2 class="light">About this Project</h2> -->
		<main class="">
			<section id="what" class="about-section">
				<h2 class="light">What is this?</h2>
				<p class="about-paragraph">
					The Gendered Project is a growing library of gendered words in the
					English dictionary.
				</p>
				<p class="about-paragraph">
					Early in 2017, during a discussion with my friends, I used the word
					fuckboy to describe a man I had dealt with. I wondered out loud why it
					took so long for a word that so aptly describes a common type of man
					to be used in popular culture. I thought of the myriad words that
					exist to derogatorily describe or shame sexually liberated women (as a
					social category) and the dearth of such equivalent words for men (as a
					social category). &nbsp;I decided to try and create something that
					would allow us to explore what the English language tells us about
					men, women and those who do not fit in those binaries.
				</p>

				<blockquote
					class="quote"
					cite="https://archive.org/details/p15957coll6_681_0"
				>
					<p class="about-paragraph">
						The act of naming is defining the boundaries and terms of our own
						existence and that of others. Naming embodies our judgments as
						inherent features of the objects to which we attend.
					</p>
					<cite class="citation"
						>–
						<a
							class="focusable-animate"
							href="https://archive.org/details/p15957coll6_681_0"
							>THE PROSTITUTE: Paradigmatic Woman</a
						>, Julia P. Stanley</cite
					>
				</blockquote>
				<p class="about-paragraph">
					The words we have (and don’t) matter. They reflect the biases we
					embrace and the ideas we value. The word imbalance for negatively
					describing men’s sexuality versus women’s reflects our culture of
					gender double standards and slut-shaming. We risk reinforcing and
					perpetuating these biases and negative values if we don’t interrogate
					the words we use and why. I think exploring language in terms of how
					genders are represented and interrogating how and why words are used
					is important for understanding and challenging sexism and patriarchy.
				</p>
			</section>
			<section id="why" class="about-section">
				<h2 class="light">Why did we make this?</h2>
				<ul class="list">
					<li>
						To provide data to use in our ongoing dialogue about the
						<a
							href="https://imaginenoborders.org/pdf/zines/UnderstandingPatriarchy.pdf"
							class="focusable-animate"
						>
							patriarchy
						</a>
						and how it permeates every facet of our daily lives, all the way
						down to the words we use.
					</li>
					<li>
						To explore:
						<ul>
							<li>Which words or named phenomena are specific to a gender?</li>
							<li>
								What words for describing a certain genders exist in surplus or
								in scarcity?
							</li>
							<li>What gendered experiences do we get to create names for?</li>
							<li>
								What words have undergone
								<a
									href="https://aslstem.cs.washington.edu/topics/view/8079"
									class="focusable-animate"
								>
									semantic derogation
								</a>
								and/or sexualization?
							</li>
							<li>
								In what ways and does language in structure, content and daily
								usage reflect and help constitute sexual inequality?
							</li>
						</ul>
					</li>
					<li>
						To encourage us to think about gaps in language and the words we
						don’t have.
					</li>
				</ul>
			</section>
			<section id="who" class="about-section">
				<h2 class="light">Who are we?</h2>
				<ul class="list">
					<li>
						Idea, data collection and analysis:
						<a href="https://www.omayeli.com" class="focusable-animate">
							Omayeli Arenyeka
						</a>
					</li>
					<li>
						Design:
						<a href="https://catangui.com/">
							Sean Catangui
						</a>
					</li>
					<li>Web Development: Omayeli Arenyeka and Sean Catangui.</li>
					<li>
						Twitter icon made by
						<a
							href="https://www.flaticon.com/authors/pixel-perfect"
							title="Pixel perfect"
							>Pixel perfect</a
						>
						from
						<a href="https://www.flaticon.com/" title="Flaticon"
							>www.flaticon.com.</a
						>
					</li>
					<li>
						With help / advice / miscalleanous support from:
						<a href="https://iheanyi.com/" class="focusable-animate"> Iheanyi</a
						>,
						<a href="https://netabomani.com/" class="focusable-animate"> Neta</a
						>, and
						<a href="https://itsthisnow.com/" class="focusable-animate">
							Syd.
						</a>
					</li>
				</ul>
			</section>
			<section id="notes" class="about-section">
				<h2 class="light">Notes</h2>
				<p class="about-paragraph">
					This project is <strong>not</strong> an invitation to "even" out the
					language and create or adopt more words that punch down. I do not
					believe in replacing male domination (exerting power over and at the
					expense of others) with female domination. As
					<a
						href="https://twitter.com/theafrolegalise"
						class="focusable-animate"
					>
						Nnennaya Amuchie
					</a>
					says, “I don't want to be equal to men. Men are not the standard of
					humanity. I want to live on my own terms free from violence.”
					Therefore it’s important to note that while the word “fuckboy” has
					taken on a new, empowering meaning through its use by femmes, it’s
					roots still lie in continual rape and abuse of
					<a
						href="https://www.theatlantic.com/politics/archive/2015/02/rape-in-the-american-prison/385550/"
						class="focusable-animate"
					>
						feminized bodies
					</a>
					in the prison system. While words can be reclaimed and acquire
					positive associations, this reclamation is often done by the groups
					the word labels. There has been
					<a
						href="https://www.themarshallproject.org/2018/07/25/prison-rape-allegations-are-on-the-rise"
						class="focusable-animate"
					>
						no such reckoning
					</a>
					in the prison system.
				</p>
				<p class="about-paragraph">
					While I hope this project can provide a space for deconstructing and
					examining gendered language, it does remain within and thus a part of
					the binary gender categorical system. It is a study of it, not an
					endorsement. The existence of gendered words like sissy and tomboy
					show that the terms female and male insufficiently categorize our
					experience and there is multiplicity within gender. Their often
					negative connotations that suggest the behaviour they describe is an
					unwelcome departure from “normal,” is a part of the system of labeling
					that I want to make data available to explore.
				</p>
				<p class="about-paragraph">
					Lastly this idea is not new. In the 1970s Julia Stanley scoured the
					Oxford English dictionary and found 220 terms referring to “women as
					prostitutes” compared to 20 for men. I’m maintaining a
					<a
						href="https://github.com/gendered/reading-list/blob/master/README.md"
						class="focusable-animate"
					>
						reading list
					</a>
					of gender and language as well as a
					<a
						href="https://www.are.na/omayeli-arenyeka/gender-and-language"
						class="focusable-animate"
					>
						channel
					</a>
					of tweets, articles and other content that inspired and/or taught me.
				</p>
			</section>
			<section id="faq" class="about-section">
				<h2 class="light">FAQ</h2>
				<h4>Where is the data from?</h4>
				<p class="about-paragraph">
					The words are gotten from many different sources. Downloadable sources
					are in this
					<a
						class="about-section"
						href="https://www.are.na/omayeli-arenyeka/gendered-words-sources"
						>Are.na channel</a
					>. Otherwise words were gotten from performing data analysis and
					filtering on dictionary APIs such as
					<a href="https://www.wordnik.com">Wordnik</a>.
				</p>
				<h4>Will you be updating?</h4>
				<p class="about-paragraph">
					Yes. The library is not fixed: it can be updated, parts of it added or
					removed, or augmented with more sources at any time. Language is
					complex and software is unable of understanding all its nuances.
					Currently, the code I’ve written cannot perfectly determine whether a
					word is gendered or not. The dataset is far from perfect -- words are
					missing and misplaced. In the future I plan to create a tool for
					easily manipulating the dataset. Please sign up if you want to
					contribute in any way.
				</p>
				<h4>Can I access the data?</h4>
				<p class="about-paragraph">
					Yes. I'm still working out how to streamline this process but for now
					send a <a href="https://twitter.com/GenderedProject">DM</a> with
					information about how you intend to use the data to request. If you
					appreciate my work and can afford it, please consider
					<a href="https://paypal.me/Omayeli/10usd"> donating</a>. Keep in mind
					that the data will continously be changing. Right now you can only get
					access to a zipped json file. A public API is coming.
				</p>
				<h4>How can I contribute?</h4>
				<p class="about-paragraph">
					Right now, you can
					<a href="http://eepurl.com/gbf1Qb">
						sign up
					</a>
					and I’ll email when I have a process for contribution.
				</p>
				<h4>Future plans</h4>
				<p class="about-paragraph">
					Check out the
					<a href="https://github.com/oa495/gendered/issues">GitHub!</a>
				</p>
			</section>
		</main>
		<aside class="toc">
			<h3 class="light toc-heading">Contents</h3>
			<ul class="list">
				<li><a href="#what">What</a></li>
				<li><a href="#why">Why</a></li>
				<li><a href="#who">Who</a></li>
				<li><a href="#notes">Notes</a></li>
				<li><a href="#faq">FAQ</a></li>
			</ul>
		</aside>
		<ScrollToTopButton />
	</div>
</template>
<style lang="scss" scoped>
@import "../mixins.scss";

.about {
	display: -ms-grid;
	display: grid;
	-ms-grid-columns: 1fr 1rem 1fr 1rem 1fr 1rem 1fr 1rem 1fr 1rem 1fr 1rem 1fr
		1rem 1fr 1rem 1fr 1rem 1fr;
	grid-template-columns: repeat(10, 1fr);
	grid-gap: 1rem;

	@include break(small) {
		-ms-grid-columns: 1fr;
		grid-template-columns: 1fr;
	}

	main {
		margin-top: 0;
		-ms-grid-column: 2;
		grid-column-start: 2;
		-ms-grid-column-span: 5;
		grid-column-end: 7;

		@include break(medium) {
			-ms-grid-column: 2;
			grid-column-start: 2;
			-ms-grid-column-span: 7;
			grid-column-end: 9;
		}

		@include break(small) {
			grid-column: auto;
		}

		.about-section {
			.list {
				padding-left: 1.6rem;
				list-style-type: square;
			}

			.quote {
				padding: 1rem;

				@include break(small) {
					margin: 0;
				}

				.about-paragraph {
					font-size: 1.3rem;
					margin-top: 0;
					margin-bottom: 1.3rem;
					line-height: 1.3em;
				}
				.citation {
					font-size: 1rem;
				}
			}
		}
	}

	.toc {
		position: fixed;
		width: 37.5%;
		top: 11.777rem;
		left: 65%;

		@include break(medium) {
			left: 80%;

			.toc-heading {
				font-size: 1rem;
			}
		}

		@include break(small) {
			display: none;
		}

		.list {
			padding-left: 0;
		}
	}

	.to-top {
		position: fixed;
		right: 2.096rem;
		bottom: 2.096rem;
	}
}
</style>
<script>
import ScrollToTopButton from "@/components/ScrollToTopButton";
export default {
	name: "AboutPage",
	components: {
		ScrollToTopButton
	},
	head: {
		title: {
			inner: "The Gendered Project - About"
		}
	}
};
</script>
